/* MARK Pickleball — paddle cover upsell block (Product information)
 *
 * Rendered by snippets/mark-cover-upsell.liquid as a <mark-cover-upsell>
 * custom element. Logic is unchanged from the original custom-liquid
 * block: it adds/removes the cover variant from the cart with a line-item
 * property so the offer can be identified, and keeps the checkbox and
 * displayed price in sync with the cart.
 */

(() => {
  if (customElements.get('mark-cover-upsell')) return;

  const OFFER_PROPERTY = '_mark_cover_offer';
  const OFFER_PROPERTY_VALUE = 'true';

  class MarkCoverUpsell extends HTMLElement {
    constructor() {
      super();

      this.checkbox = this.querySelector('.mark-cover-upsell__checkbox');
      this.selected = this.querySelector('.mark-cover-upsell__selected');
      this.promoPrice = this.querySelector('.mark-cover-upsell__promo-price');

      if (!this.checkbox || !this.selected || !this.promoPrice) return;

      this.variantId = Number(this.dataset.coverVariantId);
      this.currentCurrency = this.dataset.currency;

      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
        '/';

      this.cartUrl = root + 'cart.js';
      this.addUrl = root + 'cart/add.js';
      this.changeUrl = root + 'cart/change.js';

      this.onChange = this.onChange.bind(this);
      this.syncAfterExternalCartChange = this.syncAfterExternalCartChange.bind(this);
      this.syncingExternalCart = false;

      this.checkbox.addEventListener('change', this.onChange);
      document.addEventListener('cart:updated', this.syncAfterExternalCartChange);

      this.getCart()
        .then((cart) => {
          this.updatePromoPriceFromCart(cart);

          const offerItem = this.findOfferItem(cart);

          if (offerItem) {
            this.updateVisualState(true, '✓ Paddle cover is in your cart');
          }
        })
        .catch((error) => {
          console.error('[MARK cover offer init]', error);
        });
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.syncAfterExternalCartChange);
    }

    formatLocalizedMoney(cents) {
      const amount = Number(cents) / 100;

      if (!Number.isFinite(amount)) {
        return null;
      }

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.currentCurrency,
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${this.currentCurrency}`;
      }
    }

    updatePromoPriceFromCart(cart) {
      const offerItem = this.findOfferItem(cart);

      /*
        USD always advertises the merchant-configured promo price.
        For other currencies, only show a numeric promo price when
        Shopify has actually applied a discount to this line.
      */
      if (this.currentCurrency === 'USD') {
        return;
      }

      if (
        offerItem &&
        Number.isFinite(Number(offerItem.original_price)) &&
        Number.isFinite(Number(offerItem.final_price)) &&
        Number(offerItem.final_price) < Number(offerItem.original_price)
      ) {
        const formatted = this.formatLocalizedMoney(offerItem.final_price);
        this.promoPrice.textContent = formatted || 'Special price';
      } else {
        this.promoPrice.textContent = 'Special price';
      }
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.checkbox.disabled = isLoading;
    }

    updateVisualState(isSelected, message) {
      this.checkbox.checked = isSelected;
      this.classList.toggle('is-selected', isSelected);

      this.selected.textContent = message || '';
      this.selected.style.display = message ? 'block' : '';
    }

    findOfferItem(cart) {
      return cart.items.find((item) => {
        const props = item.properties || {};

        return (
          Number(item.variant_id) === this.variantId &&
          String(props[OFFER_PROPERTY]) === OFFER_PROPERTY_VALUE
        );
      });
    }

    updateCommonCartCount(cart) {
      this.updatePromoPriceFromCart(cart);

      const count = cart.item_count;

      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
      });

      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        bubble.classList.remove('hidden');

        const visibleCount = bubble.querySelector('span[aria-hidden="true"]');

        if (visibleCount) {
          visibleCount.textContent = count;
        }
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }

    async getCart() {
      const response = await fetch(this.cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Could not load cart.');
      }

      return response.json();
    }

    async addOfferCover() {
      const existingCart = await this.getCart();
      const existingItem = this.findOfferItem(existingCart);

      if (existingItem) {
        this.updateVisualState(true, '✓ Paddle cover is already in your cart');
        return;
      }

      const response = await fetch(this.addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: this.variantId,
              quantity: 1,
              properties: { [OFFER_PROPERTY]: OFFER_PROPERTY_VALUE },
            },
          ],
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Could not add the paddle cover. Please try again.';

        try {
          const error = await response.json();

          if (error.description) {
            errorMessage = error.description;
          }
        } catch (e) {
          /* ignore parse failure, use default message */
        }

        throw new Error(errorMessage);
      }

      const updatedCart = await this.getCart();

      this.updateCommonCartCount(updatedCart);
      this.updateVisualState(true, '✓ Paddle cover added to your cart');
    }

    async removeOfferCover() {
      const cart = await this.getCart();
      const offerItem = this.findOfferItem(cart);

      if (!offerItem) {
        this.updateVisualState(false, '');
        this.updateCommonCartCount(cart);
        return;
      }

      const response = await fetch(this.changeUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: offerItem.key, quantity: 0 }),
      });

      if (!response.ok) {
        throw new Error('Could not remove the paddle cover. Please try again.');
      }

      const updatedCart = await this.getCart();

      this.updateCommonCartCount(updatedCart);
      this.updateVisualState(false, '');
    }

    async onChange(event) {
      const shouldAdd = event.target.checked;

      this.setLoading(true);

      try {
        if (shouldAdd) {
          await this.addOfferCover();
        } else {
          await this.removeOfferCover();
        }
      } catch (error) {
        console.error('[MARK cover offer]', error);
        this.updateVisualState(!shouldAdd, error.message || 'Something went wrong. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }

    /*
      If the theme or another app announces that the cart changed
      (for example, the paddle is added separately), re-read the cart
      and refresh the localized promotional price / checkbox state.
    */
    async syncAfterExternalCartChange() {
      if (this.syncingExternalCart) return;

      this.syncingExternalCart = true;

      try {
        const cart = await this.getCart();

        this.updatePromoPriceFromCart(cart);

        const offerItem = this.findOfferItem(cart);
        this.classList.toggle('is-selected', Boolean(offerItem));
        this.checkbox.checked = Boolean(offerItem);
      } catch (error) {
        console.error('[MARK cover offer cart sync]', error);
      } finally {
        this.syncingExternalCart = false;
      }
    }
  }

  customElements.define('mark-cover-upsell', MarkCoverUpsell);
})();
